import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import type { DesktopAppId } from "./apps";

export const workbenchSections = ["overview", "task", "asset", "review", "collection", "decision", "dependency", "routine", "recipe"] as const;
export type WorkbenchSection = typeof workbenchSections[number];
export type DesktopWindowContext =
  | { kind: "explorer"; rootId: "host"; path: string; filePath?: string }
  | { kind: "workbench"; projectId?: string; section: WorkbenchSection; query: string; detailId?: string };
type Scope = { windowId: string; appId: DesktopAppId; navigationSearch: string; initialContext?: DesktopWindowContext; reportContext: (context: DesktopWindowContext) => void };
const Context = createContext<Scope | null>(null);

export function DesktopWindowProvider({ windowId, appId, navigationSearch = "", initialContext, onContext, children }: {
  windowId: string; appId: DesktopAppId; navigationSearch?: string; initialContext?: DesktopWindowContext;
  onContext: (id: string, context: DesktopWindowContext) => void; children: ReactNode;
}) {
  const reportContext = useCallback((context: DesktopWindowContext) => onContext(windowId, context), [windowId, onContext]);
  const value = useMemo(() => ({ windowId, appId, navigationSearch, initialContext, reportContext }), [windowId, appId, navigationSearch, initialContext, reportContext]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useDesktopWindow() { return useContext(Context); }

export const openDesktopWindowEvent = "base-sul-os:open-window";
export type OpenDesktopWindowDetail = { appId: DesktopAppId; search?: string; newInstance?: boolean };
export function openDesktopWindow(detail: OpenDesktopWindowDetail) {
  window.dispatchEvent(new CustomEvent<OpenDesktopWindowDetail>(openDesktopWindowEvent, { detail }));
}
